import axios from 'axios';
import URL_CONST from '../../config/url';
import {PagingParams} from '../../types/dataService/PagingParams';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  CategoryList,
  DatasetMetadataBinaryItem,
  DatasetMetadataLabels,
  DatasetMetadataLabelItem,
  DatasetMetadataResponse,
} from '../../types/dataService/DatasetMetadata';
import {useEffect, useState} from 'react';
import {chunk, flatten, isEmpty} from 'lodash';
import {ItemType} from './FileViewer';
import {
  formatBinariesToLocal,
  getLabel,
  getLabelMap,
} from '../../store/dataService/datasetMetaDataUtil';
import {FILE_TYPE, VIEW_TYPE} from './const';
import {
  getHeaders,
  getMetadata,
  LabelMap,
  MAX_METADATA_INFO,
} from '../../store/dataService';
import {DataPrepareTypeStrDS} from '../../types/dataset/DatasetGetAnnotationsResponse';
import {StudioDatasetViewerProps} from './StudioDatasetViewer';

export const DEFAULT_PAGE_PARAMS = {
  page: 0,
  size: 50,
  sort: ['created', 'asc'],
};

export const DEFAULT_PAGE_PARAMS_LABEL = {
  ...DEFAULT_PAGE_PARAMS,
  size: 20,
};

export const DEFAULT_PAGE_PARAMS_LIST = {
  ...DEFAULT_PAGE_PARAMS,
  size: 100,
};
export const DEFAULT_RSQL = [
  {id: 'image and video', rsql: 'type=in=(binary/image,binary/video)'},
];
export const IMAGE_AND_VIDEO_RSQL = [
  {
    id: 'binary/video',
    rsql: 'type==binary/video',
  },
  {
    id: 'binary/image',
    rsql: 'type==binary/image',
  },
];

export const DEFAULT_FIELDS = 'id,created,mediaType,originalPath,size,updated,batchId';
export const FIELDS_LIST = DEFAULT_FIELDS + ',width,height';

export type DatasetBinary = {
  list: ItemType[]; // flat list of all binary result
  group: DatasetBinaryResponse[];
};

export type DatasetBinaryResponse = RSQLProps & {
  list: DatasetMetadataBinaryItem[];
  formattedBinaries: ItemType[];
  totalElements: number;
  totalPages?: number;
};

export type RSQLProps = {
  id: string;
  rsql: string;
};

type DatasetLoaderProps = {
  datasetId?: string | null;
  method?: DataPrepareTypeStrDS;
  categories?: CategoryList;

  version?: number;
  previewSize?: number;
  pageParams?: PagingParams;
  //Category Ids
  rsql?: RSQLProps[];
  fields?: string;
  // Force update to call mutate
  mutateInfoCount?: number;
  mutateBinaryCount?: number;
  mutateCategoryCount?: number;
  mutateLabelCount?: number;
  showLabels: boolean;
} & Pick<StudioDatasetViewerProps, 'view'>;

export const INITIAL_METADATA = {
  list: [],
  formattedBinaries: [],
  totalElements: 0,
  totalPages: undefined,
};

export const useDatasetLoader = ({
  datasetId,
  method,
  categories,
  version,
  pageParams = DEFAULT_PAGE_PARAMS,
  previewSize = 90,
  rsql = DEFAULT_RSQL,
  fields = DEFAULT_FIELDS,
  showLabels = false,
  view,
}: DatasetLoaderProps) => {
  // main data
  const [binaryIds, setBinaryIds] = useState<string[] | null>(null);
  const [allBinaries, setAllBinaries] = useState<DatasetBinary | null>(null);
  const [binaries, setBinaries] = useState<DatasetBinary | null>(null);
  //  - only activates on showLabels=true
  const [labels, setLabels] = useState<DatasetMetadataLabels | undefined>();

  // derivatives
  const [hasVideoAndImage, setHasVideoAndImage] = useState<boolean>(false);
  const [labelMap, setLabelMap] = useState<LabelMap>({});

  const binaryFetcher = async (query: RSQLProps) =>
    axios
      .get<DatasetMetadataResponse>(URL_CONST.DSS_DATASET_METADATA, {
        headers: await getHeaders(),
        params: {
          datasetId: datasetId || '',
          page: pageParams.page,
          size: pageParams.size,
          sort: pageParams.sort?.join(),
          rsql: query.rsql,
          fields,
        },
      })
      .then(res => ({...res.data.body, ...query}));

  const multiBinaryFetcher = () => {
    return Promise.all(rsql.map(query => binaryFetcher(query))).then(res => res);
  };

  const labelFetcherForClassification = (binaryIds: string) =>
    getMetadata({
      datasetId: datasetId || '',
      rsql: `type=="annotation/label";binaryId=in=(${binaryIds})`,
      fields: 'id,category,binaryId',
      pageable: MAX_METADATA_INFO,
    });

  const labelFetcherForDetection = (binaryIds: string) =>
    getMetadata({
      datasetId: datasetId || '',
      rsql: `type=="annotation/bounding_box";binaryId=in=(${binaryIds})`,
      fields: 'id,binaryId,category,topLeftX,topLeftY,width,height',
      pageable: MAX_METADATA_INFO,
    });

  const {
    data: binaryResponse,
    error: binaryError,
    mutate: mutateBinaries,
  }: {
    data?: DatasetBinaryResponse[];
    error?: any;
    mutate: any;
  } = useSWR<any>(
    [datasetId, version, previewSize, pageParams, rsql, fields],
    rsql.length > 1 ? multiBinaryFetcher : null
  );

  function getBinaryKey(
    pageIndex: number,
    previousPageData: DatasetBinaryResponse | null
  ) {
    const totalPages = previousPageData?.totalPages || 0;
    if (pageIndex > totalPages || view === VIEW_TYPE.LABEL) {
      return null;
    }
    const url = new URL(URL_CONST.DSS_DATASET_METADATA, window.location.origin);
    url.searchParams.set('datasetId', datasetId || '');
    version && url.searchParams.set('version', (version || 0).toString());
    url.searchParams.set('page', pageIndex.toString());
    url.searchParams.set('size', pageParams.size.toString());
    pageParams.sort && url.searchParams.set('sort', pageParams.sort?.join() || '');
    url.searchParams.set('fields', fields);
    url.searchParams.set('rsql', rsql[0].rsql);

    return url.toString();
  }

  async function fetcher(url: string) {
    const {data} = await axios.get(url, {headers: await getHeaders()});
    return data.body;
  }
  const {
    data: binaryInfiniteResponse,
    error: binaryInfiniteError,
    setSize,
    mutate: mutateInfiniteBinaries,
  } = useSWRInfinite(getBinaryKey, fetcher);

  const loadMoreBinaries = async () => {
    if (datasetId && view !== VIEW_TYPE.LABEL) {
      // use infinite loader only for list/grid views
      const csize = binaryInfiniteResponse?.length || 0;
      await setSize(csize + 1);
    }
  };

  const multiLabelFetcher = (idGroups: string[]) => {
    const fetcher =
      method === DataPrepareTypeStrDS.CLASSIFICATION
        ? labelFetcherForClassification
        : labelFetcherForDetection;

    return Promise.all(idGroups.map(group => fetcher(group))).then(res => res);
  };

  const {data: labelResponse, error: labelError, mutate: mutateLabels} = useSWR<any>(
    [datasetId, version, binaryIds, showLabels, method],
    () => {
      if (binaryIds && binaryIds.length && showLabels) {
        // Have to split the label fetch since the BE has a limit on how much params or binary you can reference. Otherwise it breaks the server. Splitting it by 100 elements per group
        const group = chunk(binaryIds, 100).map(el => el.join());
        return multiLabelFetcher(group);
      }
      return null;
    }
  );

  const checkHasVideoAndImage = (items: ItemType[]) => {
    return (
      items.some(el => el.type === FILE_TYPE.VIDEO) &&
      items.some(el => el.type === FILE_TYPE.IMAGE)
    );
  };

  // TODO: consolidate same processing below
  useEffect(() => {
    if (datasetId && binaryInfiniteResponse) {
      // generate UI usable data
      // for some reasons, this binaryInfiniteResponse doesn't return id and rsql if rsql is 1 of the 2
      const group = binaryInfiniteResponse.map(res => ({
        ...res,
        id: res?.id || res?.list?.[0]?.type,
        rsql: res?.rsql || `type==${res?.list?.[0]?.type}`,
        formattedBinaries: formatBinariesToLocal(res.list, datasetId),
      }));
      // // flatten the binary data to a one dimensinal list
      const list = flatten(group.map(res => res.formattedBinaries));
      setBinaries({
        list,
        group,
      });
      setBinaryIds(list.map(el => el.id));
      setHasVideoAndImage(checkHasVideoAndImage(list));
    }
  }, [binaryInfiniteResponse, datasetId]);

  useEffect(() => {
    if (datasetId && binaryResponse) {
      // generate UI usable data
      const group = binaryResponse.map(res => ({
        ...res,
        formattedBinaries: formatBinariesToLocal(res.list, datasetId),
      }));
      // flatten the binary data to a one dimensinal list
      const list = flatten(group.map(res => res.formattedBinaries));
      setBinaries({
        list,
        group,
      });
      // using binaryResponse since the value only changes when the rsql length is greater than one
      // and in this case, it is fetching separately (image and video)
      setAllBinaries({
        list,
        group,
      });

      setBinaryIds(list.map(el => el.id));
      setHasVideoAndImage(checkHasVideoAndImage(list));
    }
  }, [binaryResponse, datasetId]);

  useEffect(() => {
    if (datasetId && labelResponse) {
      let list: DatasetMetadataLabelItem[] = [];
      (labelResponse as DatasetMetadataLabels[]).forEach(el => {
        list = list.concat(el.list);
      });
      setLabels({
        list,
        totalElements: list.length,
        totalPages: 1,
      });
    } else {
      setLabels(undefined);
    }
  }, [labelResponse, datasetId]);

  // Create labelMap
  useEffect(() => {
    if ((binaryResponse || binaryInfiniteResponse) && labels) {
      setLabelMap(getLabelMap(labels.list) as LabelMap);
    } else {
      setLabelMap({});
    }
  }, [binaryResponse, binaryInfiniteResponse, labels]);

  // Propagate labels to binary data
  useEffect(() => {
    if (!isEmpty(labelMap) && binaries) {
      // generate UI usable data
      const group =
        binaries?.group.map(el => ({
          ...el,
          formattedBinaries:
            el.formattedBinaries.map(item => ({
              ...item,
              label: getLabel(item.id, labelMap, categories, method),
            })) || [],
        })) || [];
      // flatten the binary data to a one dimensinal list
      const list = flatten(group.map(el => el.formattedBinaries));
      setBinaries({
        list,
        group,
      });
    }
    // eslint-disable-next-line
  }, [labelMap]);

  return {
    allBinaries,
    binaries,
    labels,
    isLoading: !binaryError && !binaryResponse && view === VIEW_TYPE.LABEL,
    isInfiniteLoading:
      !binaryInfiniteError && !binaryInfiniteResponse && view !== VIEW_TYPE.LABEL,
    isError: !!binaryError,
    metadataError: binaryError,
    labelError,
    hasVideoAndImage,
    labelMap,
    loadMoreBinaries,
    refreshBinaries: view === VIEW_TYPE.LABEL ? mutateBinaries : mutateInfiniteBinaries,
    refreshLabels: mutateLabels,
  };
};
